import { persistPromoCode } from 'lib/promos';

export interface OpenCartOptions {
  skus?: string[];
  promoCode?: string;
}

interface RunOpenCartDeps {
  cart: Record<string, unknown>;
  addItemsToCart: (
    items: Array<{ productCode: string; quantity?: number }>,
  ) => Promise<unknown>;
  setPromoCode: (code: string) => Promise<unknown>;
  openPanel: () => void;
}

// Fresh carts read the persisted promo during creation; existing carts apply it after the add.
export const runOpenCart = (
  { skus = [], promoCode }: OpenCartOptions,
  deps: RunOpenCartDeps,
): void => {
  if (promoCode) {
    persistPromoCode(promoCode);
  }

  const skusToAdd = skus.filter((sku) => !deps.cart[sku]);
  const hadExistingCart = Object.keys(deps.cart).length > 0;

  void (async () => {
    if (skusToAdd.length > 0) {
      await deps.addItemsToCart(skusToAdd.map((sku) => ({ productCode: sku })));
    }
    if (promoCode && hadExistingCart) {
      await deps.setPromoCode(promoCode);
    }
  })().catch((err) => {
    console.error('Error adding items to cart:', err);
  });

  deps.openPanel();
};

/**
 * Parses legacy /cart URL strings (e.g. product-data buyUrl entries like
 * '/cart?cart=syn-wk,dm-wk&promo_code=X') into openCart options.
 */
export const parseOpenCartUrl = (url: string): OpenCartOptions => {
  let urlObj: URL;
  try {
    urlObj = new URL(url, window.location.origin);
  } catch (err) {
    console.error('Failed to parse cart URL:', url, err);
    return {};
  }

  const skus =
    urlObj.searchParams
      .get('cart')
      ?.split(',')
      .map((sku) => sku.trim())
      .filter(Boolean) ?? [];
  const promoCode = urlObj.searchParams.get('promo_code') ?? undefined;

  return { skus, promoCode };
};
